





















































import Captcha from '@integrations/002-Captcha/Captcha.vue';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import flatten from 'flat';
// @ts-ignore
import ClientOnly from 'vue-client-only';
import debounce from 'lodash/debounce';
import IFormInput from '@frontend/types/modules/form/IFormInput';
import IFormLayout from '@frontend/types/modules/form/IFormLayout';
import ButtonComponent from '@components/004-ButtonComponent/ButtonComponent.vue';
import RichText from '@components/003-RichText/RichText.vue';
import Heading from '@components/base/Heading.vue';
import axios, { AxiosResponse } from 'axios';
import ImageComponent from '@components/001-ImageComponent/ImageComponent.vue';
import ModalComponent from '@components/013-Modal/Modal.vue';
import IImage from '@frontend/types/base/IImage';
import Sentry from '@libraries/sentry/browser';
import { areVisibilityContraintsFulfilled } from '@frontend/utils/Form';
import FormLayout from './components/FormLayout.vue';

// SCSS modules are not supported by fractal, so a progressive enhancement approach is required
const colors: Record<string, string> = {
	'haribo-orange': 'FF8200',
	'haribo-grey-1': 'C7C9C7'
};

/*
TODO: does not work with vue require extension: alternative?
if (process.env.CONTEXT !== 'fractal') {
	// eslint-disable-next-line
	colors = require('../../../assets/css/variables/_colors.scss');
}
*/
interface ISubmitResponseData {
	success: boolean;
	messages: Record<string, string[]>;
}

declare type State = 'input' | 'submitting' | 'success' | 'error';

@Component({
	componentId: 'M033',
	hydration: true,
	components: {
		ClientOnly,
		Heading,
		RichText,
		FormLayout,
		ButtonComponent,
		ImageComponent,
		ModalComponent,
		Captcha
	}
})
export default class Form extends Vue {
	@Prop(String)
	protected readonly headline!: string;

	@Prop({ type: Number, default: 2 })
	protected readonly headlineLevel!: number;

	@Prop(String)
	protected readonly copy!: string;

	@Prop(String)
	protected readonly successMessage!: string;

	@Prop(Object)
	protected readonly successImage!: IImage;

	@Prop(String)
	protected readonly formSlug!: string;

	@Prop(Object)
	protected readonly inputsById!: Record<string, IFormInput>;

	@Prop(Object)
	protected readonly layout!: IFormLayout[];

	@Prop(String)
	protected readonly submissionUrl!: string;

	@Prop(Object)
	protected readonly dateFormats!: Record<string, string>;

	protected captchaSolution: string | null = null;

	/**
	 * Dictionary of inputId => errors, where errors can be string[] for regular fields or Record<string, string>[] for multi-fields
	 *
	 * e.g.
	 * {
	 * 	email: ['E-Mail darf nicht leer sein'],
	 * 	consent: ['Bitte akzeptieren Sie die Nutzungsbedingungen und Datenschutzbestimmungen.'],
	 * 	JobSelection: [
	 * 		{
	 * 			office: 'Bitte einen Standort auswählen'
	 * 		}
	 * 	],
	 * 	'': ['Globaler Fehler']
	 * }
	 *
	 */
	@Prop({ type: Object, default: null })
	protected readonly errors!: Record<string, (string | Record<string, string>)[]> | null;

	protected readonly colors: Record<string, string> = colors;

	protected state: State = 'input';

	protected submissionProgress: number = 0;

	protected internalInputsById: Record<string, IFormInput> = {};

	protected globalError: string | null = null;

	protected debouncedScrollToFirstError = debounce(this.scrollToFirstError, 200, { leading: true, trailing: false });

	private successModal: any;

	protected created(): void {
		this.resetInputs();

		if (this.errors) {
			this.showErrors(this.errors);
		}
	}

	/**
	 * Replace tokens in predefined field values
	 */
	protected replaceGlobalContentProperties(input: string): string {
		let inputWithReplacements: string = input;

		// Replace all global content property tokens
		Object.keys(this.flattenedGlobalContentProperties).forEach((key: string): void => {
			inputWithReplacements = inputWithReplacements.replace(`{${key}}`, this.flattenedGlobalContentProperties[key]);
		});

		// Replace all query parameters
		Object.keys(this.queryParameters).forEach((key: string): void => {
			inputWithReplacements = inputWithReplacements.replace(`{${key}}`, this.queryParameters[key]);
		});

		return inputWithReplacements;
	}

	protected resetInputs(): void {
		Object.keys(this.inputsById).forEach((inputId: string): void => {
			this.$set(this.internalInputsById, inputId, {
				...this.inputsById[inputId],
				value:
					typeof this.inputsById[inputId].value === 'string'
						? this.replaceGlobalContentProperties(this.inputsById[inputId].value)
						: this.inputsById[inputId].value,
				hasError: false
			});
		});

		if (this.$refs && this.$refs.form) {
			(this.$refs.form as HTMLFormElement).reset();
		}
	}

	/**
	 * Submits the form
	 */
	protected async submit(): Promise<void> {
		this.state = 'submitting';

		// Reset progress and errors
		this.submissionProgress = 0;
		this.globalError = null;

		const formData: FormData = new FormData();

		const inputs = Object.values(this.internalInputsById);

		inputs.forEach((input: IFormInput): void => {
			// do not include invisible input's values
			if (!areVisibilityContraintsFulfilled(input, inputs)) {
				return;
			}

			if (Array.isArray(input.value)) {
				input.value.forEach((value: any): void => {
					formData.append(`${input.name}[]`, value);
				});
			} else if (typeof input.value === 'object' && input.value !== null) {
				Object.keys(input.value).forEach((key: string): void => {
					formData.append(`${input.name}[${key}]`, input.value[key]);
				});
			} else if (input.value !== undefined) {
				if (input.value === null) {
					formData.append(input.name, '');
				} else {
					formData.append(input.name, input.value);
				}
			}
		});

		// append captcha solution
		if (this.captchaSolution) {
			formData.append('captcha-solution', this.captchaSolution);
		}

		const config = {
			onUploadProgress: (progressEvent: ProgressEvent): void => {
				this.submissionProgress = progressEvent.loaded / progressEvent.total;
			}
		};

		try {
			const response: AxiosResponse = await axios.post(this.submissionUrl, formData, config);

			if (response.status === 200) {
				const responseData: ISubmitResponseData = response.data as ISubmitResponseData;

				if (responseData.success === true) {
					this.state = 'success';
					this.resetInputs();
				} else {
					if (responseData.messages) {
						this.showErrors(responseData.messages as any);
						this.scrollToFirstError();
					}

					this.state = 'error';

					this.captchaSolution = null;
					this.captcha.reset();
				}
			} else {
				this.state = 'error';
				this.globalError = this.i18n.unknownError;

				this.captchaSolution = null;
				this.captcha.reset();

				Sentry.captureException(
					new Error(
						`Could not send form. HTTP Status: ${response.status}, Response body: ${
							response.data
						}, headers: ${JSON.stringify(response.headers)}`
					)
				);
			}
		} catch (error) {
			this.state = 'error';
			this.globalError = this.i18n.unknownError;

			this.captchaSolution = null;
			this.captcha.reset();

			Sentry.captureException(error);
		}
	}

	protected onCaptchaSolved(solution: string): void {
		this.captchaSolution = solution;
	}

	/**
	 * global.contentProperties as flattened object in dotA.dotB notation
	 */
	protected get flattenedGlobalContentProperties(): Record<string, any> {
		if (this.global.contentProperties) {
			return flatten(this.global.contentProperties);
		}

		return {};
	}

	protected get queryParameters(): Record<string, any> {
		if (typeof window === 'undefined') return {};

		const uri: string = window.location.search.substring(1);
		const searchParams: URLSearchParams = new URLSearchParams(uri);

		const queryParameters: Record<string, any> = {};
		[...searchParams.keys()].forEach((key: string): void => {
			queryParameters[key] = searchParams.get(key);
		});

		return queryParameters;
	}

	protected get hasInputsWithErrors(): boolean {
		const inputs = Object.values(this.internalInputsById);

		return Object.keys(this.internalInputsById).some((inputId: string): boolean => {
			if (
				this.internalInputsById[inputId].error &&
				areVisibilityContraintsFulfilled(this.internalInputsById[inputId], inputs)
			) {
				if (typeof this.internalInputsById[inputId].error === 'object') {
					// error is an object of errors for multi-field sub fields
					const errorObject: Record<string, string> = this.internalInputsById[inputId].error as Record<string, string>;
					return Object.keys(errorObject).some((errorProperty: string): boolean => !!errorObject[errorProperty]);
				}
				return true;
			}
			return false;
		});
	}

	protected get submitButtonStyles(): Record<string, any> {
		const submitButtonStyles: Record<string, any> = {};
		if (this.state !== 'submitting') return submitButtonStyles;

		const progressPerc1: string = `${this.submissionProgress * 100}%`;
		const progressPerc2: string = `${Math.min(100, this.submissionProgress * 100 + 15)}%`;

		// create a wobbling gradient effect
		const gradientAngle: number = 90 - Math.sin(this.submissionProgress * 2 * Math.PI * 3) * 10;

		submitButtonStyles.backgroundImage = `linear-gradient(${gradientAngle}deg, #${colors['haribo-orange']} ${progressPerc1}, #${colors['haribo-grey-1']} ${progressPerc2})`;

		return submitButtonStyles;
	}

	protected get captcha(): Captcha {
		return this.$refs.captcha as Captcha;
	}

	@Watch('state')
	protected async onStateChange(newState: State): Promise<void> {
		if (newState === 'success') {
			if (!this.successModal) {
				const Modal = (await import('../../01-components/013-Modal/Modal.client')).default;
				this.successModal = new Modal((this.$refs.successModal as Vue).$el as HTMLElement);
			}

			this.successModal.openModal();
		}
	}

	private showErrors(errors: Record<string, (string | Record<string, string>)[]>) {
		Object.keys(this.internalInputsById).forEach((inputId: string): void => {
			const errorsForInput: (string | Record<string, string>)[] | undefined =
				errors[this.internalInputsById[inputId].name];

			let consolidatedErrorsForInput: Record<string, string> | null = null;

			if (errorsForInput && errorsForInput.length && typeof errorsForInput[0] === 'object') {
				// errorsForInput is an array of error objects for a multi-field sub fields
				(errorsForInput as Record<string, string>[]).forEach((error: Record<string, string>): void => {
					Object.keys(error).forEach((key: string): void => {
						if (!error[key]) return;

						consolidatedErrorsForInput = consolidatedErrorsForInput || {};
						consolidatedErrorsForInput[key] = consolidatedErrorsForInput[key]
							? `${consolidatedErrorsForInput[key]}<br>${error[key]}`
							: error[key];
					});
				});

				Vue.set(this.internalInputsById[inputId], 'error', consolidatedErrorsForInput);
			} else {
				// errorsForInput is an array of error strings for a regular field
				Vue.set(this.internalInputsById[inputId], 'error', errorsForInput ? errorsForInput.join('<br>') : null);
			}
		});

		if (errors['']) {
			const globalErrors: string[] = [];

			errors[''].forEach((error: string | Record<string, string>): void => {
				if (typeof error === 'object') {
					globalErrors.push(...Object.values(error));
				} else {
					globalErrors.push(error);
				}
			});

			this.globalError = globalErrors.join('<br />');
		}
	}

	private scrollToFirstError(): void {
		this.$nextTick((): void => {
			const firstErrorInput: HTMLElement | null = document.querySelector(
				'input:invalid, textarea:invalid, select:invalid'
			);

			if (firstErrorInput) {
				firstErrorInput.focus();

				const scrollTarget: Element = firstErrorInput.closest('.has-error') || firstErrorInput;

				// Check if the parent of the focused input is in the viewport, otherwise scroll it in (so the label is visible)
				if (scrollTarget.getBoundingClientRect().top < 0) {
					scrollTarget.scrollIntoView();
				}
			}
		});
	}
}
